const TRADING_DAYS_PER_YEAR = 252;

const safeDivide = (numerator, denominator) => {
  if (denominator === 0 || denominator === null || denominator === undefined) {
    return null;
  }
  return numerator / denominator;
};

// const toPercentage = (value) => (value === null || value === undefined ? null : value * 100); // Unused

export const normalizeSeriesToIndex = (series) => {
  if (!Array.isArray(series) || series.length === 0) {
    return [];
  }

  const base = series[0].close;

  if (!base || Number.isNaN(base)) {
    return [];
  }

  return series.map((point) => ({
    ...point,
    indexValue: (point.close / base) * 100
  }));
};

export const calculateDailyReturns = (series) => {
  if (!Array.isArray(series) || series.length < 2) {
    return [];
  }

  const returns = [];

  for (let i = 1; i < series.length; i += 1) {
    const current = series[i];
    const previous = series[i - 1];

    if (
      current?.close === null ||
      current?.close === undefined ||
      previous?.close === null ||
      previous?.close === undefined
    ) {
      // Skip if either price is missing
      continue;
    }

    const dailyReturn = current.close / previous.close - 1;

    if (Number.isFinite(dailyReturn)) {
      returns.push({
        timestamp: current.timestamp,
        value: dailyReturn
      });
    }
  }

  return returns;
};

const calculateAverage = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
};

const calculateStandardDeviation = (values) => {
  if (!Array.isArray(values) || values.length < 2) {
    return null;
  }

  const mean = calculateAverage(values);
  if (mean === null) {
    return null;
  }

  const variance =
    values.reduce((acc, val) => acc + (val - mean) ** 2, 0) / (values.length - 1);

  return Math.sqrt(variance);
};

const calculateMaxDrawdown = (series) => {
  if (!Array.isArray(series) || series.length === 0) {
    return null;
  }

  let peak = series[0].close;
  let maxDrawdown = 0;

  for (let i = 1; i < series.length; i += 1) {
    const price = series[i].close;
    if (price === null || price === undefined) {
      continue;
    }

    if (price > peak) {
      peak = price;
    }

    const drawdown = (price - peak) / peak;

    if (drawdown < maxDrawdown) {
      maxDrawdown = drawdown;
    }
  }

  return Math.abs(maxDrawdown);
};

export const formatPercentage = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${(value * 100).toFixed(digits)}%`;
};

export const calculateAssetStatistics = (series) => {
  const returns = calculateDailyReturns(series).map((point) => point.value);

  if (returns.length === 0) {
    return {
      averageReturn: null,
      volatility: null,
      maxDrawdown: null
    };
  }

  const avgDailyReturn = calculateAverage(returns);
  const dailyStdDev = calculateStandardDeviation(returns);
  const maxDrawdown = calculateMaxDrawdown(series);

  const annualizedReturn =
    avgDailyReturn !== null ? avgDailyReturn * TRADING_DAYS_PER_YEAR : null;
  const annualizedVolatility =
    dailyStdDev !== null ? dailyStdDev * Math.sqrt(TRADING_DAYS_PER_YEAR) : null;

  return {
    averageReturn: annualizedReturn,
    volatility: annualizedVolatility,
    maxDrawdown
  };
};

export const calculateCorrelation = (seriesA, seriesB) => {
  const returnsA = calculateDailyReturns(seriesA);
  const returnsB = calculateDailyReturns(seriesB);

  if (returnsA.length === 0 || returnsB.length === 0) {
    return null;
  }

  // Align by timestamp
  const mapB = new Map(returnsB.map((item) => [item.timestamp, item.value]));
  const alignedA = [];
  const alignedB = [];

  returnsA.forEach((item) => {
    const match = mapB.get(item.timestamp);
    if (match !== undefined) {
      alignedA.push(item.value);
      alignedB.push(match);
    }
  });

  if (alignedA.length < 2) {
    return null;
  }

  const meanA = calculateAverage(alignedA);
  const meanB = calculateAverage(alignedB);

  if (meanA === null || meanB === null) {
    return null;
  }

  let covariance = 0;
  let varianceA = 0;
  let varianceB = 0;

  for (let i = 0; i < alignedA.length; i += 1) {
    const diffA = alignedA[i] - meanA;
    const diffB = alignedB[i] - meanB;

    covariance += diffA * diffB;
    varianceA += diffA ** 2;
    varianceB += diffB ** 2;
  }

  const denominator = Math.sqrt(varianceA * varianceB);
  if (denominator === 0) {
    return null;
  }

  return covariance / denominator;
};

export const buildCorrelationMatrix = (assetSeriesMap) => {
  const assetIds = Object.keys(assetSeriesMap);
  const matrix = {};

  assetIds.forEach((idA) => {
    matrix[idA] = {};

    assetIds.forEach((idB) => {
      if (idA === idB) {
        matrix[idA][idB] = 1;
      } else {
        matrix[idA][idB] = calculateCorrelation(
          assetSeriesMap[idA],
          assetSeriesMap[idB]
        );
      }
    });
  });

  return matrix;
};

export const simulatePortfolio = (assetSeriesMap, weights) => {
  const assetIds = Object.keys(assetSeriesMap);
  const validWeights = { ...weights };

  const totals = assetIds.reduce(
    (acc, id) => {
      const weight = weights[id];
      if (weight !== null && weight !== undefined) {
        acc.sum += weight;
        acc.count += 1;
      }
      return acc;
    },
    { sum: 0, count: 0 }
  );

  if (totals.sum <= 0) {
    const equalWeight = 1 / assetIds.length;
    assetIds.forEach((id) => {
      validWeights[id] = equalWeight;
    });
  } else {
    assetIds.forEach((id) => {
      const rawWeight = weights[id] || 0;
      validWeights[id] = rawWeight / totals.sum;
    });
  }

  const dailyReturnSeries = {};

  assetIds.forEach((id) => {
    dailyReturnSeries[id] = calculateDailyReturns(assetSeriesMap[id]);
  });

  const timestamps = dailyReturnSeries[assetIds[0]].map((item) => item.timestamp);

  const portfolioReturns = timestamps
    .map((timestamp, index) => {
      let combined = 0;

      for (let i = 0; i < assetIds.length; i += 1) {
        const id = assetIds[i];
        const returns = dailyReturnSeries[id];
        if (!returns[index]) {
          return null;
        }
        combined += validWeights[id] * returns[index].value;
      }

      return combined;
    })
    .filter((value) => value !== null);

  if (portfolioReturns.length === 0) {
    return {
      normalizedWeights: validWeights,
      expectedReturn: null,
      volatility: null,
      sharpeRatio: null
    };
  }

  const avgDailyReturn = calculateAverage(portfolioReturns);
  const dailyStdDev = calculateStandardDeviation(portfolioReturns);

  const annualizedReturn =
    avgDailyReturn !== null ? avgDailyReturn * TRADING_DAYS_PER_YEAR : null;
  const annualizedVolatility =
    dailyStdDev !== null ? dailyStdDev * Math.sqrt(TRADING_DAYS_PER_YEAR) : null;
  const sharpeRatio =
    annualizedReturn !== null && annualizedVolatility
      ? safeDivide(annualizedReturn, annualizedVolatility)
      : null;

  return {
    normalizedWeights: validWeights,
    expectedReturn: annualizedReturn,
    volatility: annualizedVolatility,
    sharpeRatio
  };
};

export const toPercentageDisplay = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }
  return `${(value * 100).toFixed(digits)}%`;
};






